import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import React, { FC, useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Field } from 'api/tasks';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { ConditionalVariables } from 'components/molecules/variables/conditional-variables';
import { SimpleVariablesPopover } from 'components/molecules/variables/simple-variables-select';
import { TemplateObjectType } from 'data/templates/api';
import { useCKVariablesEvents } from 'hooks/use-ck-variable-events';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { isChildNode } from 'utils/is-child-node';
import { queueMacrotask } from 'utils/macrotask';

export enum VariablesMetaType {
  Automations = 'AUTOMATIONS',
}

export interface AutomationMetaForCkEditor {
  nestedFields: Field[];
  fields: Field[];
  type: VariablesMetaType.Automations;
}

export type VariablesMeta = AutomationMetaForCkEditor;

interface Props {
  children: any;
  editor: Editor;
  attachTo?: HTMLElement | null;
  updateParentElementLocation: (data) => void;
  projectId?: number;
  templateType?: TemplateObjectType;
  allowConditionalVariables?: boolean;
  meta?: VariablesMeta;
}

enum VariablesType {
  Simple = 'Simple',
  Conditional = 'Conditional',
}

export const CKVariablesPopup: FC<Props> = ({
  editor,
  attachTo,
  children,
  updateParentElementLocation,
  templateType,
  allowConditionalVariables,
  projectId,
  meta,
}) => {
  const [activeMentionQuery, setActiveMentionQuery] = useState<string>('');
  const [activeMentionInput, setActiveMentionInput] = useState<string>('');
  const [showMentionPopup, setMentionPopupVisibility] =
    useState<boolean>(false);

  const [type, setType] = useState<VariablesType | undefined>();
  const translate = useTranslate();
  const isChild = isChildNode(['ck-variables-popup', 'ck-editor__editable']);

  useEffect(() => {
    setType(
      activeMentionQuery.includes('{')
        ? VariablesType.Simple
        : activeMentionQuery.includes('%') && allowConditionalVariables
        ? VariablesType.Conditional
        : undefined,
    );
  }, [activeMentionInput, activeMentionQuery, allowConditionalVariables]);

  const handleCKVariablesEvents = useCallback(
    (e, data, input) => {
      if (e.name === 'variablesPopup:render') {
        const newPositions = {
          left: data.position.left,
          top: data.position.top,
        };

        updateParentElementLocation?.(newPositions);
        queueMacrotask(() => {
          setMentionPopupVisibility(true);
        });
      } else if (e.name === 'variablesPopup:remove') {
        setMentionPopupVisibility(false);
      }

      if (e.name === 'variablesPopup:query') {
        if (!showMentionPopup) {
          queueMacrotask(() => {
            setMentionPopupVisibility(true);
          });
        }
        setActiveMentionQuery(data);
        setActiveMentionInput(input);
      }
    },
    [showMentionPopup, updateParentElementLocation],
  );

  const onOutsideClick = useCallback(() => {
    setMentionPopupVisibility(false);
    // Clearing variables marker characters {{ if present.
    const { model } = editor;

    editor.model.change((writer: any) => {
      const mentionMarker = model.markers.get('variables');

      if (!mentionMarker) {
        return;
      }

      // Create a range on matched text.
      const end = model.createPositionAt(model.document.selection.focus);
      const start = model.createPositionAt(mentionMarker.getStart());
      const range = model.createRange(start, end);
      const selection = writer.createSelection(range);

      model.deleteContent(selection);
    });
  }, [editor]);

  const onSimpleVariableSelect = useCallback(
    ({ fieldName, fieldNameForBackend, realTimeValue, objectType }) => {
      setMentionPopupVisibility(false);

      const { model } = editor;

      editor.model.change((writer: any) => {
        const mentionMarker = model.markers.get('variables');

        if (!mentionMarker) {
          return;
        }

        // Create a range on matched text.
        const end = model.createPositionAt(model.document.selection.focus);
        const start = model.createPositionAt(mentionMarker.getStart());
        const range = model.createRange(start, end);
        const selection = writer.createSelection(range);

        model.deleteContent(selection);

        if (projectId) {
          if (realTimeValue) {
            const isHtml = /<[a-z][\s\S]*>/i.test(realTimeValue);

            if (isHtml) {
              const viewFragment = editor.data.processor.toView(realTimeValue);
              const modelFragment = editor.data.toModel(viewFragment);

              model.insertContent(modelFragment);
            } else {
              model.insertContent(writer.createText(`${realTimeValue}`));
            }
          }
        } else {
          const snippet = writer.createElement('simpleVariables', {
            fieldName: `${
              objectType
                ? `${translate(
                    `source_type.${objectType}` as TranslationKey,
                  )} → `
                : ''
            }${fieldName}`,
            fieldNameForBackend,
            fallback: '',
            templateType,
            ...(meta || {}),
          });

          model.insertContent(snippet);
          editor?.editing.view.focus();
        }
      });
    },
    [editor, meta, projectId, templateType, translate],
  );

  useCKVariablesEvents(handleCKVariablesEvents, editor);

  if (!type) {
    return null;
  }

  switch (type) {
    case VariablesType.Simple:
      return ReactDOM.createPortal(
        <OutsideClickHandler allowed={isChild} onOutsideClick={onOutsideClick}>
          <SimpleVariablesPopover
            visible={showMentionPopup}
            onSelect={onSimpleVariableSelect}
            projectId={projectId}
            attachTo={attachTo}
            templateType={templateType}
            fields={meta?.fields}
            nestedFields={meta?.nestedFields}
            queryInput={activeMentionQuery}
            editor={editor}
          >
            {children}
          </SimpleVariablesPopover>
        </OutsideClickHandler>,
        document.getElementById('rl-mention-popup') || document.body,
      );

    case VariablesType.Conditional:
      return ReactDOM.createPortal(
        <ConditionalVariables
          editor={editor}
          templateType={templateType}
          onClose={() => {
            setActiveMentionQuery('');
            editor?.editing.view.focus();
          }}
        />,
        document.body,
      );

    default:
      return null;
  }
};
